import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { ButtonBase, CardContent, Grid, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';

// project import
import useHeaderMenu from 'hooks/useHeaderMenu';

import MenuPopper from './MenuPopper';
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';

import MerchantAvatar from 'components/MerchantAvatar';

import { ThemeMode, IconButton, Merchant } from '@trendiolive/trendio-selfcare-ui-kit';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();
  const { anchorRef, merchant, open, selectedIndex, value, handleChange, handleClose, handleListItemClick, handleLogout, handleToggle } =
    useHeaderMenu();

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <MenuButton anchorRef={anchorRef} merchant={merchant} open={open} handleToggle={handleToggle} />
      <MenuPopper open={open} anchorRef={anchorRef} onClose={handleClose}>
        <>
          <MenuHeader merchant={merchant} handleLogout={handleLogout} />
          <MenuTabs value={value} handleChange={handleChange} />
          <TabPanel value={value} index={0} dir={theme.direction}>
            <ProfileTab handleLogout={handleLogout} />
          </TabPanel>
          <TabPanel value={value} index={1} dir={theme.direction}>
            <SettingTab selectedIndex={selectedIndex} handleListItemClick={handleListItemClick} />
          </TabPanel>
        </>
      </MenuPopper>
    </Box>
  );
}

type MenuButtonProps = Readonly<{
  anchorRef: React.MutableRefObject<any>;
  merchant: Merchant | null;
  open: boolean;
  handleToggle: () => void;
}>;

function MenuButton({ anchorRef, merchant, open, handleToggle }: MenuButtonProps) {
  const theme = useTheme();
  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';
  return (
    <ButtonBase
      sx={{
        p: 0.25,
        bgcolor: open ? iconBackColorOpen : 'transparent',
        borderRadius: 1,
        '&:hover': { bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.lighter' },
        '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 }
      }}
      aria-label="open profile"
      ref={anchorRef}
      aria-controls={open ? 'profile-grow' : undefined}
      aria-haspopup="true"
      onClick={handleToggle}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ p: 0.5 }}>
        <MerchantAvatar {...{ merchant }} size={32} sx={{ fontSize: '0.875rem' }} />
        <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
          {merchant?.name}
        </Typography>
      </Stack>
    </ButtonBase>
  );
}

type MenuHeaderProps = Readonly<{
  merchant: Merchant | null;
  handleLogout: () => Promise<void>;
}>;

function MenuHeader({ merchant, handleLogout }: MenuHeaderProps) {
  return (
    <CardContent sx={{ px: 2.5, pt: 3 }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <MerchantAvatar {...{ merchant }} size={32} sx={{ fontSize: '0.875rem' }} />
            <Stack>
              <Typography variant="body2" color="text.secondary">
                {merchant?.name}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        <Grid>
          <Tooltip title="Logout">
            <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogout}>
              <LogoutOutlined />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </CardContent>
  );
}

type MenuTabsProps = Readonly<{
  value: number;
  handleChange: (event: React.SyntheticEvent, newValue: number) => void;
}>;

function MenuTabs({ value, handleChange }: MenuTabsProps) {
  const a11yProps = (index: number) => {
    return {
      id: `profile-tab-${index}`,
      'aria-controls': `profile-tabpanel-${index}`
    };
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Tabs variant="fullWidth" value={value} onChange={handleChange} aria-label="profile tabs">
        <Tab
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            textTransform: 'capitalize'
          }}
          icon={<UserOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
          label={<FormattedMessage id="account" />}
          {...a11yProps(0)}
        />
        <Tab
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            textTransform: 'capitalize'
          }}
          icon={<QuestionCircleOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
          label={<FormattedMessage id="support" />}
          {...a11yProps(1)}
        />
      </Tabs>
    </Box>
  );
}

type TabPanelProps = Readonly<{
  children?: ReactNode;
  dir?: string;
  index: number;
  value: number;
}>;

// tab panel wrapper
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}
